import type { FC, ReactNode } from 'react'
import type { ProductSummary } from '@/types'

import Link from 'next/link'
import { useState } from 'react'
import { ShoppingBag } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import QuantityStepper from '@/components/shop/quantity-stepper'
import { useCart } from '@/components/shop/cart-provider'

interface ProductPurchaseProps {
    product: ProductSummary
}

/*
 * Option picker, quantity and add to cart for one product. The page renders
 * it with key={product.id} so the choices reset between products.
 */
const ProductPurchase: FC<ProductPurchaseProps> = ({ product }): ReactNode => {
    const { addItem } = useCart()
    const hasOptions = product.options.length > 0
    const [variant, setVariant] = useState<string | null>(product.options.length === 1 ? product.options[0] : null)
    const [quantity, setQuantity] = useState(1)
    const [error, setError] = useState<string | null>(null)

    const add = () => {
        if (hasOptions && !variant) {
            setError("Pick an option above first.")

            return
        }

        setError(null)
        addItem(product, hasOptions ? variant : null, quantity)
    }

    return (
        <div className='mt-8'>
            {hasOptions && (
                <fieldset>
                    <legend className='text-sm font-semibold'>
                        {product.options_label
                            ? (
                                <span data-mdlfy-cms={`products:${product.id}:options_label`}>
                                    {product.options_label}
                                </span>
                            )
                            : 'Option'}
                    </legend>

                    <div className='mt-3 flex flex-wrap gap-2'>
                        {product.options.map(option => (
                            <label
                                key={option}
                                className={cn(
                                    'cursor-pointer rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold transition-colors select-none hover:border-primary/40',
                                    'has-checked:border-primary has-checked:bg-primary has-checked:text-primary-foreground',
                                    'has-focus-visible:ring-3 has-focus-visible:ring-ring/50',
                                    !product.in_stock && 'pointer-events-none opacity-50'
                                )}
                            >
                                <input
                                    type='radio'
                                    name={`variant-${product.id}`}
                                    value={option}
                                    checked={variant === option}
                                    disabled={!product.in_stock}
                                    onChange={() => {
                                        setVariant(option)
                                        setError(null)
                                    }}
                                    className='sr-only'
                                />

                                {option}
                            </label>
                        ))}
                    </div>
                </fieldset>
            )}

            <div className={cn('flex flex-wrap items-center gap-3', hasOptions && 'mt-6')}>
                <QuantityStepper
                    value={quantity}
                    onChange={setQuantity}
                    label={product.name}
                />

                <Button
                    size='lg'
                    variant='accent'
                    disabled={!product.in_stock}
                    onClick={add}
                    className='min-w-0 flex-1 sm:min-w-56 sm:flex-none'
                >
                    <ShoppingBag className='size-5' />
                    {product.in_stock ? 'Add to cart' : 'Sold out'}
                </Button>
            </div>

            {error && (
                <p role='alert' className='mt-3 text-sm font-semibold text-destructive'>
                    {error}
                </p>
            )}

            {!product.in_stock && (
                <p className='mt-3 text-sm text-muted-foreground'>
                    Sold out right now.{' '}

                    <Link
                        href={{ pathname: '/contact', query: { topic: 'availability' } }}
                        className='font-semibold text-foreground underline underline-offset-4'
                    >
                        Ask us when it is back
                    </Link>
                </p>
            )}
        </div>
    )
}

export default ProductPurchase
